export type AnalyticsPlatform = 'web' | 'pwa';
export type ResultCountBucket = '0' | '1' | '2-5' | '6-10' | '11-25' | '26+';
export type QueryLengthBucket = '0' | '1-2' | '3-5' | '6-10' | '11+';
export type CalendarSource = 'shows' | 'pokemon' | 'onepiece' | 'magic' | 'riftbound' | 'lorcana' | 'fleshandblood' | 'unknown';
export type AnalyticsSurface = 'search_bar' | 'quick_filter' | 'calendar' | 'calendar_card' | 'tournament_session' | 'event_modal' | 'event_detail' | 'saved_events' | 'sets_page' | 'set_detail' | 'promo_banner' | 'related_event' | 'sponsor_popup';
export type SponsorPopupAction = 'shown' | 'dismissed' | 'sponsor_clicked' | 'opted_out';
export type SponsorId = 'cardrevive' | 'cardhub';

export interface ProductEventMap {
  discovery_search_performed: {
    surface: 'search_bar';
    query_length_bucket: QueryLengthBucket;
  };
  discovery_filter_changed: {
    surface: 'quick_filter' | 'calendar' | 'sets_page';
    filter_type: 'date_range' | 'entry' | 'event_type' | 'game' | 'release_status' | 'clear';
    filter_value: string;
    selected: boolean;
  };
  discovery_results_viewed: {
    surface: 'calendar';
    calendar_source: CalendarSource;
    query_length_bucket: QueryLengthBucket;
    result_count_bucket: ResultCountBucket;
    has_filters: boolean;
  };
  event_opened: {
    surface: 'calendar_card' | 'tournament_session' | 'event_modal' | 'event_detail' | 'saved_events' | 'promo_banner' | 'related_event';
    event_key: string;
    calendar_source: CalendarSource;
  };
  event_save_changed: {
    surface: 'calendar_card' | 'tournament_session' | 'event_modal' | 'event_detail' | 'saved_events';
    event_key: string;
    save_scope: 'device' | 'account';
    change: 'saved' | 'unsaved';
    reminder_set: boolean;
  };
  event_action_completed: {
    surface: 'event_modal' | 'event_detail' | 'saved_events';
    event_key: string;
    action: 'registration' | 'directions' | 'share' | 'calendar';
    method: 'official_link' | 'google' | 'outlook' | 'ics' | 'native' | 'clipboard' | 'maps';
  };
  saved_events_returned: {
    surface: 'saved_events';
    save_scope: 'device' | 'account' | 'mixed';
    result_count_bucket: ResultCountBucket;
  };
  release_opened: {
    surface: 'sets_page' | 'set_detail';
    release_key: string;
  };
  sponsor_popup: {
    surface: 'sponsor_popup';
    action: SponsorPopupAction;
    sponsor?: SponsorId;
  };
}

export type ProductEventName = keyof ProductEventMap;
export type ProductEventProperties<Name extends ProductEventName> = ProductEventMap[Name];
export type AnalyticsEnvelope = Record<string, string | number | boolean> & {
  schema_version: 1;
  platform_source: AnalyticsPlatform;
};

const VALUE_PATTERN = /^[a-z0-9][a-z0-9_:+-]{0,99}$/;
const EVENT_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,119}$/;
const SOURCES = new Set<CalendarSource>(['shows', 'pokemon', 'onepiece', 'magic', 'riftbound', 'lorcana', 'fleshandblood', 'unknown']);
const OPEN_SURFACES = new Set<AnalyticsSurface>(['calendar_card', 'tournament_session', 'event_modal', 'event_detail', 'saved_events', 'promo_banner', 'related_event']);
const SAVE_SURFACES = new Set<AnalyticsSurface>(['calendar_card', 'tournament_session', 'event_modal', 'event_detail', 'saved_events']);
const ACTION_SURFACES = new Set<AnalyticsSurface>(['event_modal', 'event_detail', 'saved_events']);
const RESULT_BUCKETS = new Set<ResultCountBucket>(['0', '1', '2-5', '6-10', '11-25', '26+']);
const QUERY_BUCKETS = new Set<QueryLengthBucket>(['0', '1-2', '3-5', '6-10', '11+']);
const SPONSOR_POPUP_ACTIONS = new Set<SponsorPopupAction>(['shown', 'dismissed', 'sponsor_clicked', 'opted_out']);
const SPONSOR_IDS = new Set<SponsorId>(['cardrevive', 'cardhub']);

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
const isEnum = <Value extends string>(values: Set<Value>) => (value: unknown): value is Value => (
  typeof value === 'string' && values.has(value as Value)
);
const isSafeValue = (value: unknown): value is string => (
  typeof value === 'string' && VALUE_PATTERN.test(value)
);
const isEntityKey = (value: unknown): value is string => (
  typeof value === 'string' && EVENT_KEY_PATTERN.test(value)
);

type Guard = (value: unknown) => boolean;
type Definition = { required: readonly string[]; properties: Record<string, Guard> };

const DEFINITIONS: Record<ProductEventName, Definition> = {
  discovery_search_performed: {
    required: ['surface', 'query_length_bucket'],
    properties: { surface: isEnum(new Set(['search_bar'])), query_length_bucket: isEnum(QUERY_BUCKETS) },
  },
  discovery_filter_changed: {
    required: ['surface', 'filter_type', 'filter_value', 'selected'],
    properties: {
      surface: isEnum(new Set(['quick_filter', 'calendar', 'sets_page'])),
      filter_type: isEnum(new Set(['date_range', 'entry', 'event_type', 'game', 'release_status', 'clear'])),
      filter_value: isSafeValue,
      selected: isBoolean,
    },
  },
  discovery_results_viewed: {
    required: ['surface', 'calendar_source', 'query_length_bucket', 'result_count_bucket', 'has_filters'],
    properties: {
      surface: isEnum(new Set(['calendar'])), calendar_source: isEnum(SOURCES),
      query_length_bucket: isEnum(QUERY_BUCKETS), result_count_bucket: isEnum(RESULT_BUCKETS), has_filters: isBoolean,
    },
  },
  event_opened: {
    required: ['surface', 'event_key', 'calendar_source'],
    properties: { surface: isEnum(OPEN_SURFACES), event_key: isEntityKey, calendar_source: isEnum(SOURCES) },
  },
  event_save_changed: {
    required: ['surface', 'event_key', 'save_scope', 'change', 'reminder_set'],
    properties: {
      surface: isEnum(SAVE_SURFACES), event_key: isEntityKey,
      save_scope: isEnum(new Set(['device', 'account'])), change: isEnum(new Set(['saved', 'unsaved'])), reminder_set: isBoolean,
    },
  },
  event_action_completed: {
    required: ['surface', 'event_key', 'action', 'method'],
    properties: {
      surface: isEnum(ACTION_SURFACES), event_key: isEntityKey,
      action: isEnum(new Set(['registration', 'directions', 'share', 'calendar'])),
      method: isEnum(new Set(['official_link', 'google', 'outlook', 'ics', 'native', 'clipboard', 'maps'])),
    },
  },
  saved_events_returned: {
    required: ['surface', 'save_scope', 'result_count_bucket'],
    properties: {
      surface: isEnum(new Set(['saved_events'])), save_scope: isEnum(new Set(['device', 'account', 'mixed'])), result_count_bucket: isEnum(RESULT_BUCKETS),
    },
  },
  release_opened: {
    required: ['surface', 'release_key'],
    properties: { surface: isEnum(new Set(['sets_page', 'set_detail'])), release_key: isEntityKey },
  },
  sponsor_popup: {
    // sponsor is intentionally not required: it only applies to the
    // sponsor_clicked action, and is omitted (rather than sanitized to a
    // placeholder) for shown, dismissed and opted_out.
    required: ['surface', 'action'],
    properties: {
      surface: isEnum(new Set(['sponsor_popup'])),
      action: isEnum(SPONSOR_POPUP_ACTIONS),
      sponsor: isEnum(SPONSOR_IDS),
    },
  },
};

export const sanitizeProductEvent = (
  name: string,
  input: Record<string, unknown>,
  platform: AnalyticsPlatform,
): AnalyticsEnvelope | null => {
  const definition = DEFINITIONS[name as ProductEventName];
  if (!definition || !input || typeof input !== 'object') return null;

  const sanitized: AnalyticsEnvelope = { schema_version: 1, platform_source: platform };
  for (const [key, guard] of Object.entries(definition.properties)) {
    const value = input[key];
    if (guard(value) && (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number')) {
      sanitized[key] = value;
    }
  }

  return definition.required.every((key) => key in sanitized) ? sanitized : null;
};

export const bucketQueryLength = (value: string): QueryLengthBucket => {
  const length = value.trim().length;
  if (length === 0) return '0';
  if (length <= 2) return '1-2';
  if (length <= 5) return '3-5';
  if (length <= 10) return '6-10';
  return '11+';
};

export const bucketResultCount = (count: number): ResultCountBucket => {
  if (count <= 0) return '0';
  if (count === 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 10) return '6-10';
  if (count <= 25) return '11-25';
  return '26+';
};
